import React, { useState, useEffect } from 'react';
import {
  PlayIcon,
  StopIcon,
  PauseIcon,
  CalendarIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { attendanceAPI } from '../services/api';

const CheckInOut = ({ className = '' }) => {
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [commentAction, setCommentAction] = useState(null);
  const [comment, setComment] = useState('');

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchTodayAttendance();
  }, []);

  const fetchTodayAttendance = async () => {
    try {
      setLoading(true);
      
      // Add timezone header to request
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await attendanceAPI.getToday({
        headers: {
          'x-timezone': userTimezone
        }
      });
      
      // Handle different API response structures
      const attendanceData = response.data.attendance || response.data.data || response.data;
      setTodayAttendance({
        ...attendanceData,
        timezone_info: {
          user_timezone: response.data.user_timezone,
          local_date: response.data.local_date,
          current_time: response.data.current_time
        }
      });
      setError(null);
    } catch (error) {
      console.error('Error fetching today attendance:', error);
      setError('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  };

  const clearMessages = () => {
    setError(null);
    setSuccessMessage(null);
  };

  const openCommentModal = (action) => {
    setCommentAction(action);
    setComment('');
    setShowCommentModal(true);
  };

  const closeCommentModal = () => {
    setShowCommentModal(false);
    setCommentAction(null);
    setComment('');
  };

  const handleAction = async (action) => {
    try {
      setActionLoading(true);
      clearMessages();

      let response;
      const data = comment ? { comments: comment } : {};
      
      // Add timezone header to all attendance actions
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const config = {
        headers: {
          'x-timezone': userTimezone
        }
      };

      switch (action) {
        case 'clockIn':
          response = await attendanceAPI.clockIn(data, config);
          break;
        case 'clockOut':
          response = await attendanceAPI.clockOut(data, config);
          break;
        case 'startBreak':
          response = await attendanceAPI.startBreak(data, config);
          break;
        case 'endBreak':
          response = await attendanceAPI.endBreak(data, config);
          break;
        default:
          throw new Error('Invalid action');
      }

      setSuccessMessage(response.data.message);
      closeCommentModal();
      
      // Refresh attendance data
      await fetchTodayAttendance();
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (error) {
      console.error('Error performing action:', error);
      setError(error.response?.data?.message || 'Failed to perform action');
    } finally {
      setActionLoading(false);
    }
  };

  const getCurrentStatus = () => {
    if (!todayAttendance) {
      return 'Not clocked in';
    }

    // Check the attendance record directly instead of events
    if (todayAttendance.clock_out) {
      return 'Clocked out';
    } else if (todayAttendance.break_start && !todayAttendance.break_end) {
      return 'On break';
    } else if (todayAttendance.clock_in) {
      return 'Clocked in';
    } else {
      return 'Not clocked in';
    }
  };

  const getWorkedHours = () => {
    if (!todayAttendance) {
      return '0:00';
    }

    // Use real-time hours if available (for active sessions)
    if (todayAttendance.real_time_hours) {
      const totalHours = parseFloat(todayAttendance.real_time_hours);
      const hours = Math.floor(totalHours);
      const minutes = Math.round((totalHours % 1) * 60);
      return `${hours}:${minutes.toString().padStart(2, '0')}`;
    }

    // Use the total_hours from the record if available (for completed sessions)
    if (todayAttendance.total_hours) {
      const totalHours = parseFloat(todayAttendance.total_hours);
      const hours = Math.floor(totalHours);
      const minutes = Math.round((totalHours % 1) * 60);
      return `${hours}:${minutes.toString().padStart(2, '0')}`;
    }

    // Fallback calculation from clock_in and current time for active sessions
    if (todayAttendance.clock_in && !todayAttendance.clock_out) {
      const clockInTime = todayAttendance.clock_in;
      const currentTime = new Date().toLocaleTimeString('en-GB', { hour12: false });
      
      const [clockInHour, clockInMin] = clockInTime.split(':').map(Number);
      const [currentHour, currentMin] = currentTime.split(':').map(Number);
      
      const clockInTotal = clockInHour * 60 + clockInMin;
      const currentTotal = currentHour * 60 + currentMin;
      
      let totalMinutes = currentTotal - clockInTotal;
      
      // Handle next day scenario (rare)
      if (totalMinutes < 0) {
        totalMinutes += 24 * 60;
      }
      
      // Subtract break time if present
      if (todayAttendance.break_start) {
        const [breakStartHour, breakStartMin] = todayAttendance.break_start.split(':').map(Number);
        const breakStartTotal = breakStartHour * 60 + breakStartMin;
        
        if (todayAttendance.break_end) {
          // Break completed
          const [breakEndHour, breakEndMin] = todayAttendance.break_end.split(':').map(Number);
          const breakEndTotal = breakEndHour * 60 + breakEndMin;
          totalMinutes -= (breakEndTotal - breakStartTotal);
        } else {
          // Currently on break
          totalMinutes -= (currentTotal - breakStartTotal);
        }
      }

      const hours = Math.floor(Math.max(0, totalMinutes) / 60);
      const minutes = Math.max(0, totalMinutes) % 60;
      return `${hours}:${minutes.toString().padStart(2, '0')}`;
    }

    return '0:00';
  };

  const getAvailableActions = () => {
    if (!todayAttendance) {
      return {
        canClockIn: true,
        canClockOut: false,
        canStartBreak: false,
        canEndBreak: false
      };
    }

    // Check attendance record state instead of events
    if (todayAttendance.clock_out) {
      // Already clocked out for the day
      return {
        canClockIn: true,
        canClockOut: false,
        canStartBreak: false,
        canEndBreak: false
      };
    } else if (todayAttendance.break_start && !todayAttendance.break_end) {
      // Currently on break
      return {
        canClockIn: false,
        canClockOut: false,
        canStartBreak: false,
        canEndBreak: true
      };
    } else if (todayAttendance.clock_in) {
      // Clocked in and not on break
      return {
        canClockIn: false,
        canClockOut: true,
        canStartBreak: true,
        canEndBreak: false
      };
    } else {
      // Not clocked in yet
      return {
        canClockIn: true,
        canClockOut: false,
        canStartBreak: false,
        canEndBreak: false
      };
    }
  };

  const { canClockIn, canClockOut, canStartBreak, canEndBreak } = getAvailableActions();

  if (loading) {
    return (
      <div className={className}>
        <div className="animate-pulse">
          <div className="h-4 theme-bg-secondary rounded mb-4"></div>
          <div className="h-8 theme-bg-secondary rounded mb-4"></div>
          <div className="h-10 theme-bg-secondary rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div data-testid="check-in-out-widget" className={className}>
        <div className="space-y-6">
          {/* Current Time and Date */}
          <div data-testid="time-display" className="text-center">
            <div data-testid="current-time" className="text-3xl font-bold theme-text-primary">
              {currentTime.toLocaleTimeString('en-GB', {
                hour12: false,
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              })}
            </div>
            <div data-testid="current-date" className="text-sm theme-text-secondary flex items-center justify-center mt-1">
              <CalendarIcon className="w-4 h-4 mr-1" />
              {currentTime.toLocaleDateString('en-GB', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
              {todayAttendance?.timezone_info?.user_timezone && (
                <span data-testid="timezone-display" className="ml-2 text-xs theme-bg-secondary px-2 py-1 rounded">
                  {todayAttendance.timezone_info.user_timezone}
                </span>
              )}
            </div>
          </div>

          {/* Current Status */}
          <div data-testid="status-display" className="text-center">
            <div data-testid="current-status" className="text-lg font-medium theme-text-primary">
              {getCurrentStatus()}
            </div>
            <div data-testid="worked-hours" className="text-sm theme-text-secondary">
              Worked today: {getWorkedHours()}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div data-testid="error-message" className="bg-red-50 border border-red-200 rounded-md p-3">
              <div className="text-sm text-red-600">{error}</div>
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div data-testid="success-message" className="bg-green-50 border border-green-200 rounded-md p-3">
              <div className="text-sm text-green-600">{successMessage}</div>
            </div>
          )}

          {/* Action Buttons */}
          <div data-testid="action-buttons" className="space-y-3">
            {canClockIn && (
              <button
                data-testid="clock-in-button"
                onClick={() => openCommentModal('clockIn')}
                disabled={actionLoading}
                className="btn-primary w-full flex items-center justify-center space-x-2"
              >
                <PlayIcon className="w-4 h-4" />
                <span>Clock In</span>
              </button>
            )}

            {canClockOut && (
              <button
                data-testid="clock-out-button"
                onClick={() => openCommentModal('clockOut')}
                disabled={actionLoading}
                className="btn-secondary w-full flex items-center justify-center space-x-2"
              >
                <StopIcon className="w-4 h-4" />
                <span>Clock Out</span>
              </button>
            )}

            {canStartBreak && (
              <button
                data-testid="start-break-button"
                onClick={() => openCommentModal('startBreak')}
                disabled={actionLoading}
                className="btn-outline w-full flex items-center justify-center space-x-2"
              >
                <PauseIcon className="w-4 h-4" />
                <span>Start Break</span>
              </button>
            )}

            {canEndBreak && (
              <button
                data-testid="end-break-button"
                onClick={() => openCommentModal('endBreak')}
                disabled={actionLoading}
                className="btn-primary w-full flex items-center justify-center space-x-2"
              >
                <PlayIcon className="w-4 h-4" />
                <span>End Break</span>
              </button>
            )}

            {!canClockIn && !canClockOut && !canStartBreak && !canEndBreak && !actionLoading && (
              <div data-testid="no-actions-message" className="text-sm theme-text-secondary py-3">
                {todayAttendance?.clock_out
                  ? "You've completed your workday for today!"
                  : "No actions available at this time."
                }
              </div>
            )}

            {actionLoading && (
              <div data-testid="action-loading" className="flex items-center justify-center text-sm theme-text-secondary">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mr-2"></div>
                Processing...
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Comment Modal */}
      {showCommentModal && (
        <div data-testid="comment-modal" className="modal-overlay">
          <div className="modal-container">
            <div data-testid="modal-content" className="modal-content max-w-md">
              <div className="modal-header">
                <h3 data-testid="modal-title" className="text-lg font-medium theme-text-primary">
                  {commentAction === 'clockIn' && 'Clock In'}
                  {commentAction === 'clockOut' && 'Clock Out'}
                  {commentAction === 'startBreak' && 'Start Break'}
                  {commentAction === 'endBreak' && 'End Break'}
                </h3>
              </div>

              <div className="modal-body">
                <div className="form-group">
                  <label className="block text-sm font-medium theme-text-secondary mb-2">
                    Comments (Optional)
                  </label>
                  <textarea
                    data-testid="comment-input"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Add a comment for this action..."
                    className="form-textarea"
                    rows="3"
                    maxLength="500"
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  data-testid="modal-cancel-button"
                  onClick={closeCommentModal}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  data-testid="modal-confirm-button"
                  onClick={() => handleAction(commentAction)}
                  disabled={actionLoading}
                  className="btn-primary disabled:opacity-50"
                >
                  {actionLoading ? 'Processing...' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CheckInOut;
